import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Sparkles, AlertTriangle, Search, X, ArrowUpRight, Download, FileText } from "lucide-react";
import api, { apiError, fileUrl, openPdf } from "@/lib/api";
import { fmtDateTime } from "@/lib/helpers";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export default function Cleaners() {
  const nav = useNavigate();
  const [logs, setLogs] = useState([]);
  const [props, setProps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [f, setF] = useState({ property_id: "", cleaner: "", date_from: "", date_to: "", issues_only: false });

  useEffect(() => { api.get("/properties").then((r) => setProps(r.data)).catch(() => {}); }, []);

  const load = () => {
    setLoading(true);
    const q = new URLSearchParams();
    if (f.property_id) q.set("property_id", f.property_id);
    if (f.cleaner.trim()) q.set("cleaner", f.cleaner.trim());
    if (f.date_from) q.set("date_from", f.date_from);
    if (f.date_to) q.set("date_to", f.date_to);
    if (f.issues_only) q.set("issues_only", "true");
    api.get(`/cleaning-logs?${q.toString()}`).then((r) => setLogs(r.data)).catch((e) => toast(e)).finally(() => setLoading(false));
  };
  const toast = (e) => apiError(e);
  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [f.property_id, f.issues_only, f.date_from, f.date_to, f.cleaner]);

  const clear = () => setF({ property_id: "", cleaner: "", date_from: "", date_to: "", issues_only: false });
  const issueCount = logs.filter((l) => l.has_issue).length;
  const qstr = () => {
    const q = new URLSearchParams();
    if (f.property_id) q.set("property_id", f.property_id);
    if (f.cleaner.trim()) q.set("cleaner", f.cleaner.trim());
    if (f.date_from) q.set("date_from", f.date_from);
    if (f.date_to) q.set("date_to", f.date_to);
    if (f.issues_only) q.set("issues_only", "true");
    return q.toString();
  };
  const exportFile = async (fmt) => {
    try { await openPdf(`/cleaning-logs/export.${fmt}?${qstr()}`, true, `cleaning-records.${fmt}`); }
    catch (e) { apiError(e); }
  };

  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-6" data-testid="cleaners-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <p className="overline text-accent flex items-center gap-1.5"><Sparkles className="h-3.5 w-3.5" />Cleaning Records</p>
          <h1 className="font-head text-3xl font-extrabold tracking-tight mt-1">Cleaners</h1>
          <p className="text-sm text-muted-foreground mt-1">Every clean logged through the QR checklist across all properties.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" data-testid="export-csv" disabled={logs.length === 0} onClick={() => exportFile("csv")}><Download className="h-4 w-4 mr-1.5" />CSV</Button>
          <Button variant="outline" size="sm" data-testid="export-pdf" disabled={logs.length === 0} onClick={() => exportFile("pdf")}><FileText className="h-4 w-4 mr-1.5" />PDF</Button>
        </div>
      </div>

      <div className="bg-white border border-border rounded-md p-4 grid grid-cols-1 md:grid-cols-5 gap-3 items-end">
        <div className="md:col-span-1">
          <Label className="overline">Property</Label>
          <Select value={f.property_id || "all"} onValueChange={(v) => setF({ ...f, property_id: v === "all" ? "" : v })}>
            <SelectTrigger data-testid="filter-property" className="mt-1.5"><SelectValue placeholder="All properties" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All properties</SelectItem>
              {props.map((p) => <SelectItem key={p.id} value={p.id}>{p.title}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="overline">Cleaner name</Label>
          <div className="relative mt-1.5">
            <Search className="h-4 w-4 absolute left-2.5 top-2.5 text-muted-foreground" />
            <Input data-testid="filter-cleaner" value={f.cleaner} onChange={(e) => setF({ ...f, cleaner: e.target.value })} onKeyDown={(e) => e.key === "Enter" && load()} className="pl-8" placeholder="Search…" />
          </div>
        </div>
        <div>
          <Label className="overline">From</Label>
          <Input data-testid="filter-date-from" type="date" value={f.date_from} onChange={(e) => setF({ ...f, date_from: e.target.value })} className="mt-1.5" />
        </div>
        <div>
          <Label className="overline">To</Label>
          <Input data-testid="filter-date-to" type="date" value={f.date_to} onChange={(e) => setF({ ...f, date_to: e.target.value })} className="mt-1.5" />
        </div>
        <div className="flex items-center justify-between gap-2">
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <Switch data-testid="filter-issues-only" checked={f.issues_only} onCheckedChange={(v) => setF({ ...f, issues_only: !!v })} />
            Issues only
          </label>
          <Button variant="ghost" size="sm" data-testid="clear-filters" onClick={clear}><X className="h-4 w-4 mr-1" />Clear</Button>
        </div>
      </div>

      <div className="flex items-center gap-4 text-sm text-muted-foreground">
        <span data-testid="cleaners-count"><strong className="text-foreground">{logs.length}</strong> clean{logs.length === 1 ? "" : "s"}</span>
        {issueCount > 0 && <span className="flex items-center gap-1 text-[#D98324]"><AlertTriangle className="h-3.5 w-3.5" />{issueCount} with issues</span>}
      </div>

      {loading ? (
        <p className="overline text-muted-foreground">Loading…</p>
      ) : logs.length === 0 ? (
        <div className="bg-white border border-border rounded-md p-12 text-center">
          <Sparkles className="h-8 w-8 text-muted-foreground mx-auto mb-3" />
          <p className="text-sm text-muted-foreground">No cleaning records match these filters.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {logs.map((l) => (
            <div key={l.id} data-testid={`cleaner-log-${l.id}`} className="bg-white border border-border rounded-md p-4">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                  <button data-testid={`cleaner-log-prop-${l.id}`} onClick={() => nav(`/properties/${l.property_id}`)} className="text-sm font-semibold hover:text-accent inline-flex items-center gap-1">
                    {l.property_title}<ArrowUpRight className="h-3.5 w-3.5" />
                  </button>
                  <p className="text-xs text-muted-foreground mt-0.5">{l.cleaner_name}{l.cleaner_company ? ` · ${l.cleaner_company}` : ""} · {fmtDateTime(l.created_at)}</p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span className="text-[0.65rem] px-2 py-0.5 rounded-full bg-[#2B4C3F]/12 text-[#2B4C3F] font-medium">{l.done_count}/{l.total_count} done</span>
                  {l.has_issue && <span className="text-[0.6rem] px-2 py-0.5 rounded-full bg-[#D98324]/15 text-[#D98324] flex items-center gap-1"><AlertTriangle className="h-3 w-3" />Issue</span>}
                </div>
              </div>

              {(l.rooms && l.rooms.length > 0) ? (
                <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                  {l.rooms.map((rm, ri) => (
                    <div key={ri} className="border border-border rounded-md p-2.5">
                      <p className="overline mb-1.5">{rm.name}</p>
                      <div className="flex flex-wrap gap-1">
                        {rm.items.map((it, i) => (
                          <span key={i} className={`text-[0.6rem] px-2 py-0.5 rounded-full ${it.done ? "bg-[#2B4C3F]/12 text-[#2B4C3F]" : "bg-secondary text-muted-foreground line-through"}`}>{it.name}</span>
                        ))}
                      </div>
                      {rm.photos?.length > 0 && (
                        <div className="flex gap-1.5 flex-wrap mt-1.5">
                          {rm.photos.map((ph, pi) => <a key={pi} href={fileUrl(ph)} target="_blank" rel="noreferrer"><img src={fileUrl(ph)} alt="" className="h-12 w-12 object-cover rounded-md border border-border" /></a>)}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : l.items?.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-3">
                  {l.items.map((it, i) => <span key={i} className={`text-[0.6rem] px-2 py-0.5 rounded-full ${it.done ? "bg-[#2B4C3F]/12 text-[#2B4C3F]" : "bg-secondary text-muted-foreground line-through"}`}>{it.name}</span>)}
                </div>
              )}

              {l.notes && <p className="text-xs mt-2 text-muted-foreground whitespace-pre-wrap"><span className="overline">Notes</span> {l.notes}</p>}
              {l.has_issue && l.issue_description && <p className="text-xs mt-1 text-[#9E382A] whitespace-pre-wrap"><span className="overline">Issue</span> {l.issue_description}</p>}
              {l.photos?.length > 0 && (
                <div className="flex gap-2 flex-wrap mt-2">
                  {l.photos.map((ph, i) => <a key={i} href={fileUrl(ph)} target="_blank" rel="noreferrer"><img src={fileUrl(ph)} alt="" className="h-16 w-16 object-cover rounded-md border border-border" /></a>)}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </motion.div>
  );
}
